import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { markSubscribed } from "@/lib/sparkle";
import { User } from "@/lib/types";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (email: string, password: string, fullName: string, referralCode?: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>(null as any);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      localStorage.removeItem("nl_token");
    }
  };

  useEffect(() => {
    const init = async () => {
      if (localStorage.getItem("nl_token")) await refresh();
      setLoading(false);
    };
    init();
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("nl_token", data.access_token);
    setUser(data.user);
    markSubscribed();
    if (data.user.limited) {
      toast.warning(
        data.user.limited_reason
          ? `Your account is limited: ${data.user.limited_reason}`
          : "Your account is limited — you can read, but submissions and contributions are disabled.",
        { duration: 10000 });
    }
    return data.user as User;
  };

  const signup = async (email: string, password: string, fullName: string, referralCode?: string) => {
    const { data } = await api.post("/auth/signup", { email, password, full_name: fullName, referral_code: referralCode || null });
    localStorage.setItem("nl_token", data.access_token);
    setUser(data.user);
    markSubscribed();
    return data.user as User;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("nl_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
